import { useGetAccountsQuery } from "@/modules/account/services";
import type { IAccountTransferForm } from "@/modules/transaction/common/types";
import { AccountTransferForm } from "@/modules/transaction/components";
import {
  accountTransferValidationSchema
} from "@/modules/transaction/validations/validationSchema";
import { DATE_FORMAT, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import { CloudflareAsset, SideSheetPosition, StaticUrl, Storage } from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { CloudFlareImage, InfoLinks, PageHeader } from "@/shared/components";
import { formatDate, getFromLocalStorage } from "@/shared/helpers";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useState } from "react";

export const AccountTransfer = ({ isCopy, isEdit }: IOperationPageProps) => {
  const { t, exists } = useTranslationExists();
  const isDesktop = useMediaQuery();
  const [isSideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [isAccountNumberInvalid, setIsAccountNumberInvalid] = useState<boolean>(false);
  const [isNameUnique, setIsNameUnique] = useState<boolean>(false);
  const { data: accountsList = [] } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const initialValues: IAccountTransferForm = {
    transactionNumber: "",
    creditAccount: "",
    debitAccount: "",
    beneficiary: "",
    beneficiaryLegalStatus: null,
    scheduleDate: formatDate(new Date(), DATE_FORMAT.YYYY_MM_DD),
    amount: "",
    isSaveAsTemplate: false,
    templateName: "",
    tin: getFromLocalStorage(Storage.Tin) || "",
    purpose: "",
    isUrgent: false,
    currency: null,
    fee: 0,
    commissionAccount: "",
  };

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={accountTransferValidationSchema({
          t,
          accountsList,
          isAccountNumberInvalid,
          isNameUnique,
        })}
      >
        <PageHeader>
          {!isSideBarOpen && exists("transfer.sidebarTitle") && (
            <Button
              type="secondary"
              size="small"
              className="side-sheet-btn"
              data-testid="open-info-side-sheet"
              data-ga-id="open-info-side-sheet"
              data-ga-action="Open Account transfer info side sheet"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                ),
                alignment: "left",
              }}
              onClick={() => setSideBarOpen(!isSideBarOpen)}
            />
          )}
        </PageHeader>
        <AccountTransferForm
          isEdit={isEdit}
          isCopy={isCopy}
          accountsList={accountsList}
          setIsAccountNumberInvalid={setIsAccountNumberInvalid}
          setIsNameUnique={setIsNameUnique}
          isNameUnique={isNameUnique}
        />
      </FormContainer>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isSideBarOpen}
        onClose={() => setSideBarOpen(false)}
        title={t("transfer.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("transfer.account.sidebarMessage")}
          </Text>
          <InfoLinks
            keys={[
              StaticUrl.CorporateTariffs,
              StaticUrl.CardTariffs,
              StaticUrl.TermsAndConditions,
            ]}
          />
        </div>
      </SideSheet>
    </>
  );
};
